import React from 'react';
import styled from 'styled-components';

import ColorPickerSettings from './ColorPickerSettings';
import ButtonGroupSettings from './ButtonGroupSettings';
import InputSettingsField from './InputSettingsField';
import { StyledAddonAfter, StyledAddonBefore } from './SettingsCommonElements';
import Icon from '../../misc/Icon';

import ThemeIcons from '../../../theme/Icons';
import FormStylesOptions from '../../../constants/FormStylesOptions';

const StyledFieldWrapperRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const StyledFieldWrapper = styled.div`
  box-sizing: border-box;
  margin-bottom: 8px;
`;

const FieldStyleSettings = ({

  id,

  lineHeight,
  onLineHeightChange,

  /**
   * Auto-height manages the line height itself on a fillable PDF textarea, rescaling it on every
   * growth and restoring the base on reset. When it is on the control is rendered disabled and
   * showing the live percentage, rather than removed: a missing input reads as a setting that
   * does not exist, where a greyed one reads as a setting something else is driving.
   */
  lineHeightLocked,

  fontSize,
  onFontSizeChange,

  fontColorLabel,
  fontColor,
  onFontColorChange,

  fontStyle,
  onFontStyleChange,
  fontWeight,
  onFontWeightChange,
  textTransform,
  onTextTransformChange,
  textDecoration,
  onTextDecorationChange,

  textOverflow,
  onTextOverflowChange,

  textAlign,
  onTextAlignChange,

  margin,
  onMarginChange,

  padding,
  onPaddingChange,

  borderColorLabel,
  borderColor,
  onBorderColorChange,

  borderRadius,
  onBorderRadiusChange,

  borderWidth,
  onBorderWidthChange,

  borderStyle,
  onBorderStyleChange,

  backgroundColorLabel,
  backgroundColor,
  onBackgroundColorChange,

  zIndex,
  onZIndexChange,

}) => {

  const defaultInputStyles = { };

  const fontStyleTypes = [];

  if (onTextAlignChange) {
    fontStyleTypes.push(
      {
        type: 'left',
        icon: ThemeIcons.settings.textAlignLeft,
        active: textAlign === FormStylesOptions.textAlignLeft || !textAlign,
      }
    );

    fontStyleTypes.push(
      {
        type: 'center',
        icon: ThemeIcons.settings.textAlignCenter,
        active: textAlign === FormStylesOptions.textAlignCenter,
      }
    );

    fontStyleTypes.push(
      {
        type: 'right',
        icon: ThemeIcons.settings.textAlignRight,
        active: textAlign === FormStylesOptions.textAlignRight,
      }
    );

  }

  if (onFontWeightChange) fontStyleTypes.push({ type: 'fontWeight', icon: ThemeIcons.settings.fontWeight, active: fontWeight === FormStylesOptions.fontWeightBold });
  if (onFontStyleChange) fontStyleTypes.push({ type: 'fontStyle', icon: ThemeIcons.settings.fontStyle, active: fontStyle === FormStylesOptions.fontStyleItalic });
  if (onTextDecorationChange) fontStyleTypes.push({ type: 'textDecoration', icon: ThemeIcons.settings.textDecoration, active: textDecoration === FormStylesOptions.textDecorationUnderline });
  if (onTextTransformChange) fontStyleTypes.push({ type: 'textTransform', icon: ThemeIcons.settings.textTransform, active: textTransform === FormStylesOptions.textTransformUpperCase });
  if (onTextOverflowChange) {
    fontStyleTypes.push({ type: 'textOverflowEllipsis', icon: ThemeIcons.settings.overflowEllipsis, active: textOverflow === FormStylesOptions.textOverflowEllipsis });
    fontStyleTypes.push({ type: 'textOverflowWrap', icon: ThemeIcons.settings.overflowWrap, active: textOverflow !== FormStylesOptions.textOverflowEllipsis });
  }

  const fontStyleNode = onFontStyleChange
    || onFontWeightChange
    || onTextDecorationChange
    || onTextTransformChange
    || onTextAlignChange
    || onTextOverflowChange ? (
      <StyledFieldWrapper>
        <ButtonGroupSettings
          id={id}
          types={fontStyleTypes}
          onChange={(type) => {
            if (type === 'fontWeight') {
              onFontWeightChange(fontWeight === FormStylesOptions.fontWeightBold ?
                FormStylesOptions.fontWeightNormal : FormStylesOptions.fontWeightBold);
            } else if (type === 'fontStyle') {
              onFontStyleChange(fontStyle === FormStylesOptions.fontStyleItalic ?
                FormStylesOptions.fontStyleNormal : FormStylesOptions.fontStyleItalic);
            } else if (type === 'textDecoration') {
              onTextDecorationChange(textDecoration === FormStylesOptions.textDecorationUnderline ?
                FormStylesOptions.textDecorationNone : FormStylesOptions.textDecorationUnderline);
            } else if (type === 'textTransform') {
              onTextTransformChange(textTransform === FormStylesOptions.textTransformUpperCase ?
                FormStylesOptions.textTransformNone : FormStylesOptions.textTransformUpperCase);
            } else if (type === 'left' || type === 'center' || type === 'right') {
              onTextAlignChange(type);
            } else if (type === 'textOverflowEllipsis' || type === 'textOverflowWrap') {
              onTextOverflowChange(textOverflow === FormStylesOptions.textOverflowEllipsis ?
                FormStylesOptions.textOverflowNone : FormStylesOptions.textOverflowEllipsis);
            }
          }}
        />
      </StyledFieldWrapper>
    ) : null;

  return (
    <>
      {!onLineHeightChange ? null : (
        <StyledFieldWrapper>
          {lineHeightLocked ? (
            <InputSettingsField
              type="number"
              addonBefore={<Icon icon={ThemeIcons.settings.lineHeight} size="sm" />}
              addonAfter={<Icon icon={ThemeIcons.settings.lineHeightPercent} size="sm" />}
              value={lineHeight}
              disabled
            />
          ) : (
            <InputSettingsField
              type="number"
              addonBefore={<Icon icon={ThemeIcons.settings.lineHeight} size="sm" />}
              addonAfter={<Icon icon={ThemeIcons.settings.lineHeightPercent} size="sm" />}
              value={lineHeight}
              onChange={(id, value) => onLineHeightChange(parseFloat(value, 10))}
            />
          )}
        </StyledFieldWrapper>
      )}
      {!onFontSizeChange ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            type="number"
            addonBefore={<Icon icon={ThemeIcons.settings.fontSize} size="sm" />}
            inputStyles={defaultInputStyles}
            value={fontSize}
            onChange={(id, value) => onFontSizeChange(parseFloat(value, 10))}
          />
        </StyledFieldWrapper>
      )}
      {!onFontColorChange ? null : (
        <StyledFieldWrapper>
          <ColorPickerSettings
            id={`${id}-font-color`}
            label={fontColorLabel}
            value={fontColor}
            onChange={onFontColorChange}
          />
        </StyledFieldWrapper>
      )}
      {fontStyleNode}
      {!onBackgroundColorChange ? null : (
        <StyledFieldWrapper>
          <ColorPickerSettings
            id={`${id}-background-color`}
            label={backgroundColorLabel}
            value={backgroundColor}
            addonAfter={<StyledAddonAfter>Bg</StyledAddonAfter>}
            onChange={onBackgroundColorChange}
          />
        </StyledFieldWrapper>
      )}
      {!onBorderColorChange ? null : (
        <StyledFieldWrapper>
          <ColorPickerSettings
            id={`${id}-border-color`}
            label={borderColorLabel}
            addonAfter={<StyledAddonAfter>Border</StyledAddonAfter>}
            value={borderColor}
            onChange={onBorderColorChange}
          />
        </StyledFieldWrapper>
      )}
      {!onBorderStyleChange ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            type="dropdown"
            value={borderStyle}
            addonAfter={<StyledAddonAfter>Border Style</StyledAddonAfter>}
            options={[
              { _id: 'solid', value: 'Solid' },
              { _id: 'double', value: 'Double' },
              { _id: 'dashed', value: 'Dashed' },
            ]}
            onChange={(fieldId, value) => onBorderStyleChange(value)}
          />
        </StyledFieldWrapper>
      )}
      <StyledFieldWrapperRow>
        {!onBorderRadiusChange ? null : (
          <StyledFieldWrapper style={{ maxWidth: '50%', paddingRight: '4px' }}>
            <InputSettingsField
              type="number"
              addonBefore={<StyledAddonBefore>Br</StyledAddonBefore>}
              value={borderRadius}
              onChange={(id, value) => onBorderRadiusChange(parseFloat(value, 10))}
            />
          </StyledFieldWrapper>
        )}
        {!onBorderWidthChange ? null : (
          <StyledFieldWrapper style={{ maxWidth: '50%' }}>
            <InputSettingsField
              type="number"
              addonBefore={<StyledAddonBefore>Bw</StyledAddonBefore>}
              value={borderWidth}
              onChange={(id, value) => onBorderWidthChange(parseFloat(value, 10))}
            />
          </StyledFieldWrapper>
        )}
      </StyledFieldWrapperRow>
      {!onPaddingChange ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            type="number"
            inputStyles={defaultInputStyles}
            value={padding}
            addonAfter={<StyledAddonAfter>Padding</StyledAddonAfter>}
            onChange={(id, value) => {
              onPaddingChange(value && !Number.isNaN(value) ? parseFloat(value, 10) : 0); }}
          />
        </StyledFieldWrapper>
      )}
      {!onMarginChange ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            type="number"
            inputStyles={defaultInputStyles}
            addonAfter={<StyledAddonAfter>Margin</StyledAddonAfter>}
            value={margin}
            onChange={(id, value) => {
              onMarginChange(value && !Number.isNaN(value) ? parseFloat(value, 10) : 0);
            }}
          />
        </StyledFieldWrapper>
      )}
      {!onZIndexChange ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            type="number"
            addonBefore={<Icon icon={ThemeIcons.settings.zIndex} size="sm" />}
            inputStyles={defaultInputStyles}
            value={zIndex}
            onChange={(id, value) => {
              if (value && !Number.isNaN(value) && value < 0) onZIndexChange(1);
              else if (value && !Number.isNaN(value)) onZIndexChange(parseFloat(value, 10));
            }}
          />
        </StyledFieldWrapper>
      )}
    </>
  );
};

export default FieldStyleSettings;
